import { CommonModule } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { finalize, from, map, mergeMap, switchMap, toArray } from 'rxjs';
import { AuthService } from '../../auth/auth.service';
import { Job, PropertyInfo } from '../../model/tops.models';
import { inspectionTimestamp } from '../../mixins/date-utils';
import { ApiService, AssignedRemoteJobSummary, JobSyncConflict, JobSyncDraft, RemoteJobDetails } from '../../service/api.service';
import { ToastService } from '../../service/toast.service';
import { JobFormModalComponent } from '../shared/job-form-modal.component';
import { JobCardComponent } from './job-card.component';

@Component({
  selector: 'app-jobs-list',
  standalone: true,
  imports: [CommonModule, FormsModule, JobCardComponent, JobFormModalComponent],
  templateUrl: './jobs-list.component.html',
  styleUrl: './jobs-list.component.css',
})
export class JobsListComponent implements OnInit {
  private readonly api = inject(ApiService);
  private readonly auth = inject(AuthService);
  private readonly toast = inject(ToastService);

  jobs: Job[] = [];
  loading = true;
  searchTerm = '';
  statusFilter = 'All Jobs';
  sortBy: 'inspectionDateTime' | 'address' | 'jobNumber' = 'inspectionDateTime';
  selectedJob: Job | null = null;
  showJobModal = false;
  jobToDelete: Job | null = null;
  pendingPrefill: Record<string, unknown> | null = null;
  fetchingRemoteJob = false;
  syncingOfflineData = false;
  syncingJobId: number | string | null = null;
  syncDraft: JobSyncDraft | null = null;
  syncConflicts: JobSyncConflict[] = [];
  syncReviewOpen = false;
  pendingSyncCount = 0;
  lookupErrorMessage = '';
  downloadJobsOpen = false;
  loadingAssignedJobs = false;
  downloadingSelectedJobs = false;
  downloadProgressTotal = 0;
  downloadProgressCompleted = 0;
  downloadProgressMessage = '';
  assignedJobs: AssignedRemoteJobSummary[] = [];
  selectedDownloadJobNumbers = new Set<string>();
  private lastRemoteLookupTerm = '';
  readonly fallbackReportTypeOptions = ['Rural Report', 'CBRE Long Form', 'Capital Allowances'];

  // Uses the backend-loaded report types once available, falling back to the static list before they load.
  get supportedReportTypes(): string[] {
    const options = this.api.mcproReportTypes()
      .map((option) => option.reportType)
      .filter((reportType): reportType is string => !!reportType);
    return options.length ? options : this.fallbackReportTypeOptions;
  }

  get hasLocalJobNumberMatch(): boolean {
    const jobNumber = this.searchTerm.trim().toLowerCase();
    return !!jobNumber && this.jobs.some((job) => (job.jobNumber || '').toLowerCase() === jobNumber);
  }

  get canFetchRemoteJob(): boolean {
    return !!this.searchTerm.trim()
      && !this.loading
      && !this.fetchingRemoteJob
      && !this.hasLocalJobNumberMatch
      && this.filteredJobs.length === 0;
  }

  get filteredJobs(): Job[] {
    const term = this.searchTerm.trim().toLowerCase();
    return this.jobs
      .filter((job) => {
        const matchesSearch = !term
          || (job.jobNumber || '').toLowerCase().includes(term)
          || (job.address || '').toLowerCase().includes(term);
        const matchesStatus = this.statusFilter === 'All Jobs' || job.status === this.statusFilter;
        return matchesSearch && matchesStatus;
      })
      .sort((a, b) => {
        if (this.sortBy === 'address') return (a.address || '').localeCompare(b.address || '');
        if (this.sortBy === 'jobNumber') return (a.jobNumber || '').localeCompare(b.jobNumber || '');
        return inspectionTimestamp(a.appointmentDate, a.appointmentTime) - inspectionTimestamp(b.appointmentDate, b.appointmentTime);
      });
  }

  ngOnInit(): void {
    this.api.ensureReportTypesLoaded().subscribe();
    this.loadJobs();
    this.loadSyncStatus();
  }

  loadJobs(): void {
    this.loading = true;
    this.api.getJobs().pipe(finalize(() => this.loading = false)).subscribe({
      next: (jobs) => this.jobs = jobs,
      error: (error) => this.toast.error('Error', error?.message || 'Failed to fetch jobs.'),
    });
  }

  loadSyncStatus(): void {
    this.api.getSyncStatus().subscribe({
      next: (status) => this.pendingSyncCount = status.pendingJobCount,
      error: () => this.pendingSyncCount = 0,
    });
  }

  onSearchTermChange(): void {
    this.lastRemoteLookupTerm = '';
    this.lookupErrorMessage = '';
  }

  filterLocalJobs(event: Event): void {
    event.preventDefault();
  }

  lookupSearchJob(): void {
    const jobNumber = this.searchTerm.trim();
    if (!this.canFetchRemoteJob || this.lastRemoteLookupTerm === jobNumber) return;
    this.fetchingRemoteJob = true;
    this.lookupErrorMessage = '';
    this.api.getRemoteJobDetails(jobNumber).pipe(
      switchMap((details) => {
        const reportType = String(details.job.reportType ?? '').trim().toLowerCase();
        const isSupportedReportType = this.supportedReportTypes.some((option) => option.toLowerCase() === reportType);
        if (!isSupportedReportType || Number(details.job.mcProId) === 1) {
          throw new Error('NZ Residential Report Not Found');
        }
        return this.importRemoteJobDetails(details);
      }),
      finalize(() => this.fetchingRemoteJob = false),
    ).subscribe({
      next: (job) => {
        this.lastRemoteLookupTerm = jobNumber;
        this.jobs = [job, ...this.jobs.filter((item) => item.id !== job.id)];
        this.loadSyncStatus();
        this.toast.success('Job loaded', `Job ${jobNumber} has been added to the jobs list.`);
      },
      error: (error) => {
        this.lastRemoteLookupTerm = '';
        this.lookupErrorMessage = this.remoteLookupErrorMessage(error, jobNumber);
      },
    });
  }

  openDownloadJobs(): void {
    if (this.loadingAssignedJobs) return;
    const username = this.auth.account()?.username;
    if (!username) {
      this.toast.error('Sign in required', 'Unable to identify the signed-in user.');
      return;
    }

    this.loadingAssignedJobs = true;
    this.api.getAssignedRemoteJobs(username).pipe(
      finalize(() => this.loadingAssignedJobs = false),
    ).subscribe({
      next: (jobs) => {
        this.assignedJobs = jobs;
        this.selectedDownloadJobNumbers.clear();
        this.downloadJobsOpen = true;
      },
      error: (error) => this.toast.error('Download Jobs failed', error?.message || 'Failed to fetch assigned jobs.'),
    });
  }

  closeDownloadJobs(): void {
    if (this.downloadingSelectedJobs) return;
    this.downloadJobsOpen = false;
    this.selectedDownloadJobNumbers.clear();
  }

  isDownloadJobSelected(jobNumber: string | null | undefined): boolean {
    return !!jobNumber && this.selectedDownloadJobNumbers.has(jobNumber);
  }

  get selectableDownloadJobCount(): number {
    return this.selectableDownloadJobNumbers.length;
  }

  get areAllDownloadJobsSelected(): boolean {
    const jobNumbers = this.selectableDownloadJobNumbers;
    return jobNumbers.length > 0 && jobNumbers.every((jobNumber) => this.selectedDownloadJobNumbers.has(jobNumber));
  }

  get downloadProgressPercent(): number {
    if (!this.downloadProgressTotal) return this.downloadingSelectedJobs ? 8 : 0;
    return Math.max(8, Math.round((this.downloadProgressCompleted / this.downloadProgressTotal) * 100));
  }

  isJobAlreadyOffline(jobNumber: string | null | undefined): boolean {
    return !!jobNumber && this.jobs.some((job) => String(job.jobNumber || '') === jobNumber);
  }

  toggleAllDownloadJobs(): void {
    if (this.downloadingSelectedJobs) return;
    const jobNumbers = this.selectableDownloadJobNumbers;
    if (this.areAllDownloadJobsSelected) {
      jobNumbers.forEach((jobNumber) => this.selectedDownloadJobNumbers.delete(jobNumber));
      return;
    }
    jobNumbers.forEach((jobNumber) => this.selectedDownloadJobNumbers.add(jobNumber));
  }

  toggleDownloadJob(jobNumber: string | null | undefined): void {
    if (!jobNumber || this.isJobAlreadyOffline(jobNumber) || this.downloadingSelectedJobs) return;
    if (this.selectedDownloadJobNumbers.has(jobNumber)) {
      this.selectedDownloadJobNumbers.delete(jobNumber);
      return;
    }
    this.selectedDownloadJobNumbers.add(jobNumber);
  }

  downloadSelectedJobs(): void {
    if (!this.selectedDownloadJobNumbers.size || this.downloadingSelectedJobs) return;
    const selectedJobs = this.assignedJobs.filter((details) => {
      const jobNumber = details.jobNumber || '';
      return this.selectedDownloadJobNumbers.has(jobNumber) && !this.isJobAlreadyOffline(jobNumber);
    });
    if (!selectedJobs.length) return;

    const username = this.auth.account()?.username;
    if (!username) {
      this.toast.error('Sign in required', 'Unable to identify the signed-in user.');
      return;
    }

    this.downloadingSelectedJobs = true;
    this.downloadProgressTotal = selectedJobs.length;
    this.downloadProgressCompleted = 0;
    this.downloadProgressMessage = 'Preparing selected jobs...';
    this.api.getAssignedRemoteJobDetails(username, selectedJobs.map((job) => job.trackerId)).pipe(
      switchMap((detailsList) => from(detailsList).pipe(
        mergeMap((details) => {
          this.downloadProgressMessage = `Downloading job ${details.job.jobNumber || ''}...`;
          return this.importRemoteJobDetails(details).pipe(
            map((job) => {
              this.downloadProgressCompleted += 1;
              return job;
            }),
          );
        }, 3),
        toArray(),
      )),
      finalize(() => {
        this.downloadingSelectedJobs = false;
        this.downloadProgressTotal = 0;
        this.downloadProgressCompleted = 0;
        this.downloadProgressMessage = '';
      }),
    ).subscribe({
      next: (downloadedJobs) => {
        const downloadedIds = new Set(downloadedJobs.map((job) => job.id));
        this.jobs = [
          ...downloadedJobs,
          ...this.jobs.filter((job) => !downloadedIds.has(job.id)),
        ];
        this.loadSyncStatus();
        this.downloadJobsOpen = false;
        this.selectedDownloadJobNumbers.clear();
        this.toast.success('Jobs downloaded', `${downloadedJobs.length} job${downloadedJobs.length === 1 ? '' : 's'} added to TOPS Offline.`);
      },
      error: (error) => this.toast.error('Download Jobs failed', error?.message || 'Failed to download selected jobs.'),
    });
  }

  private get selectableDownloadJobNumbers(): string[] {
    return this.assignedJobs
      .map((details) => details.jobNumber || '')
      .filter((jobNumber) => !!jobNumber && !this.isJobAlreadyOffline(jobNumber));
  }

  private importRemoteJobDetails(details: RemoteJobDetails) {
    const existingJob = this.jobs.find((job) => String(job.jobNumber || '') === String(details.job.jobNumber || ''));
    const request = existingJob
      ? this.api.updateJob(existingJob.id, details.job)
      : this.api.createJob(details.job);

    return request.pipe(
      switchMap((job) => this.api.savePropertyInfo(job.id, {
        ...details.propertyInfo,
        jobId: job.id,
      } as PropertyInfo).pipe(
        switchMap(() => this.api.saveImportedPhotos(job.id, details.photos)),
        map(() => job),
      )),
    );
  }

  private remoteLookupErrorMessage(error: unknown, jobNumber: string): string {
    const message = error instanceof Error ? error.message : '';
    if (/nz rural report not found/i.test(message)) return 'NZ Rural Report Not Found.';
    if (/no records exist/i.test(message)) return `Job ${jobNumber} was not found or the remote service is offline.`;
    return message || `Job ${jobNumber} was not found or the remote service is offline.`;
  }

  saveAndSync(): void {
    if (this.syncingOfflineData) return;
    this.syncingOfflineData = true;
    this.api.syncOfflineDataToSqs().pipe(
      finalize(() => this.syncingOfflineData = false),
    ).subscribe({
      next: (result) => {
        this.pendingSyncCount = 0;
        this.toast.success('Save and Sync complete', `Offline data sent to SQS${result.messageId ? ` (${result.messageId})` : ''}.`);
      },
      error: (error) => this.toast.error('Save and Sync failed', error?.message || 'Failed to send offline data to SQS.'),
    });
  }

  saveAndSyncJob(job: Job): void {
    if (this.syncingJobId) return;
    this.syncingJobId = job.id;
    this.api.prepareJobSync(job).pipe(
      finalize(() => {
        if (!this.syncReviewOpen) this.syncingJobId = null;
      }),
    ).subscribe({
      next: (draft) => {
        if (draft.conflicts.length) {
          this.syncDraft = draft;
          this.syncConflicts = draft.conflicts;
          this.syncReviewOpen = true;
          this.syncingJobId = null;
          return;
        }
        this.syncResolvedJob(draft, []);
      },
      error: (error) => this.toast.error('Save and Sync failed', error?.message || 'Failed to prepare job sync.'),
    });
  }

  confirmJobSync(): void {
    if (!this.syncDraft || this.syncingJobId) return;
    this.syncResolvedJob(this.syncDraft, this.syncConflicts);
  }

  closeSyncReview(): void {
    if (this.syncingJobId) return;
    this.syncDraft = null;
    this.syncConflicts = [];
    this.syncReviewOpen = false;
  }

  valueText(value: unknown): string {
    if (value === null || value === undefined || value === '') return 'Blank';
    if (typeof value === 'string') return value.startsWith('data:image/') ? '[image data]' : value;
    return JSON.stringify(value, null, 2);
  }

  private syncResolvedJob(draft: JobSyncDraft, conflicts: JobSyncConflict[]): void {
    this.syncingJobId = draft.jobId;
    this.api.syncResolvedJobToSqs(draft, conflicts).pipe(
      finalize(() => this.syncingJobId = null),
    ).subscribe({
      next: (result) => {
        this.pendingSyncCount = Math.max(0, this.pendingSyncCount - 1);
        this.closeSyncReview();
        this.loadJobs();
        this.loadSyncStatus();
        this.toast.success('Save and Sync complete', `Job ${draft.jobNumber || draft.jobId} sent to sync${result.messageId ? ` (${result.messageId})` : ''}.`);
      },
      error: (error) => this.toast.error('Save and Sync failed', error?.message || 'Failed to sync this job.'),
    });
  }

  createJob(): void {
    this.selectedJob = null;
    this.pendingPrefill = null;
    this.showJobModal = true;
  }

  editJob(job: Job): void {
    this.selectedJob = job;
    this.showJobModal = true;
  }

  closeJobModal(): void {
    this.showJobModal = false;
  }

  onJobSaved(): void {
    this.showJobModal = false;
    this.loadJobs();
    this.loadSyncStatus();
  }

  deleteJob(job: Job): void {
    this.jobToDelete = job;
  }

  confirmDelete(): void {
    if (!this.jobToDelete) return;
    this.api.deleteJob(this.jobToDelete.id).subscribe({
      next: () => {
        this.toast.success('Success', 'Job deleted successfully.');
        this.jobToDelete = null;
        this.loadJobs();
        this.loadSyncStatus();
      },
      error: (error) => this.toast.error('Error', error?.message || 'Failed to delete job.'),
    });
  }

  archiveJob(job: Job): void {
    this.api.archiveJob(job.id).subscribe({
      next: () => { this.toast.success('Success', 'Job archived successfully.'); this.loadJobs(); this.loadSyncStatus(); },
      error: (error) => this.toast.error('Error', error?.message || 'Failed to archive job.'),
    });
  }

  completeJob(job: Job): void {
    this.updateStatus(job, 'Completed', 'Job marked as completed.');
  }

  reopenJob(job: Job): void {
    this.updateStatus(job, 'In Progress', 'Job moved back to In Progress.');
  }

  private updateStatus(job: Job, status: string, message: string): void {
    this.api.updateJob(job.id, { status }).subscribe({
      next: () => { this.toast.success('Success', message); this.loadJobs(); this.loadSyncStatus(); },
      error: (error) => this.toast.error('Error', error?.message || 'Failed to update job.'),
    });
  }
}
